// THE ZERO-FLOOR DECIDER — one place that turns "the wallet can't cover this"
// into money the client understands plus the actions that resolve it.
//
// WHY THIS EXISTS (2026-07-18): "the AVAILABLE balance
// shouldn't go below 0 — not by the admins, not by the client. We need proper
// UX/UI feedbacks and actions for it."
//
// A floor refusal is NOT a payment failure. Nothing has been charged, nothing
// is in flight, and there is nothing for the client to retry. Rendering it as a
// red error string is the dead-end this module replaces. It is a PRE-FLIGHT
// BLOCK with three resolving actions, always in this order:
//
//   1. Pay the difference by card  — the existing ONE Pay-now + wallet-toggle
//                                    checkout, with the card leg prefilled to
//                                    the shortfall. The natural continuation.
//   2. Top up the wallet           — ClientDepositForm, prefilled.
//   3. Reduce the booking          — back to the form.
//
// MONEY CONTRACT. Every sentence this module produces states plainly that
// nothing has been charged. It must never imply money moved, and it must never
// quote a netted or invented figure: all arithmetic here is done on figures the
// SERVER sent (available / committed_breakdown / shortfall), and the breakdown
// rows are rendered GROSS per the deposit-headroom rule.

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

export const fmtAed = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The one epsilon. Mirrors the server gate so a 1-fils rounding difference
// never produces "it said I could".
export const FLOOR_EPSILON = 0.005;

export const FLOOR_CODE = 'WALLET_AVAILABLE_FLOOR';

// Server codes that all mean the same thing to a client: your available balance
// does not cover this, and nothing has been charged.
const FLOOR_CODES = [FLOOR_CODE, 'INSUFFICIENT_BALANCE', 'WOULD_GO_NEGATIVE'];

const errBody = (err) => err?.response?.data || err?.data || err || {};

export const isWalletFloorError = (err) => {
    const code = errBody(err).code;
    return typeof code === 'string' && FLOOR_CODES.includes(code);
};

/**
 * Normalise a floor refusal into the shape describeShortfall() consumes.
 * Returns null when the error is not a floor refusal, so callers can chain it
 * ahead of their generic handler and fall through untouched.
 *
 * The server contract (routes/services/walletFloor.js):
 *   { code, shortfall, required, available, total_balance,
 *     committed_breakdown: [{ label, amount, count }], adjustments_due_total }
 * Older servers send only `shortfall` (or nothing but `error`), so every field
 * is read defensively and a missing figure degrades the copy rather than
 * producing a wrong number.
 */
export const readWalletFloor = (err) => {
    if (!isWalletFloorError(err)) return null;
    const d = errBody(err);
    const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
    return {
        code: d.code,
        shortfall: num(d.shortfall),
        required: num(d.required ?? d.amount ?? d.total),
        available: num(d.available ?? d.available_balance),
        totalBalance: num(d.total_balance),
        committed: Array.isArray(d.committed_breakdown) ? d.committed_breakdown : [],
        amountOwed: num(d.adjustments_due_total) || 0,
        serverMessage: d.error || d.message || null,
    };
};

/**
 * THE COPY. Given the cost of what the client is trying to do and what their
 * wallet can actually contribute, produce the headline, the "why", and the
 * ordered actions.
 *
 * `available` is passed UNCLAMPED — a legitimately negative available is the
 * P5 case and must reach the client as the real number, not as zero. Only the
 * CONTRIBUTION is floored (a negative balance cannot pay for anything), which
 * is a ceiling on an action, never a statement about their money.
 */
export const describeShortfall = ({
    cost = 0,
    available = 0,
    totalBalance = null,
    committed = [],
    amountOwed = 0,
    shortfall = null,
    canPayByCard = true,
} = {}) => {
    const costN = round2(cost);
    const availN = round2(available);
    const contribution = Math.max(0, availN);
    const shortN = round2(
        Number.isFinite(Number(shortfall)) && Number(shortfall) > 0
            ? Number(shortfall)
            : Math.max(0, costN - contribution),
    );

    const rows = (Array.isArray(committed) ? committed : [])
        .filter((r) => Number(r?.amount || 0) > FLOOR_EPSILON)
        .map((r) => ({
            label: r.label,
            amount: round2(r.amount),
            count: Number(r.count) || 0,
        }));

    const headline = `This costs ${fmtAed(costN)}. You have ${fmtAed(availN)} available, `
        + `so you are ${fmtAed(shortN)} short.`;

    // The "why" only claims something when the server told us what is
    // committed. With no breakdown we say nothing rather than guessing.
    let why = null;
    if (rows.length > 0) {
        const total = totalBalance != null ? totalBalance : null;
        const causes = rows
            .map((r) => `${fmtAed(r.amount)} is held for ${r.label.toLowerCase()}`)
            .join(', and ');
        why = total != null
            ? `Your balance is ${fmtAed(total)}, but ${causes}.`
            : `${causes.charAt(0).toUpperCase()}${causes.slice(1)}.`;
    } else if (totalBalance != null && round2(totalBalance) !== availN) {
        why = `Your balance is ${fmtAed(totalBalance)}, and the difference is already committed.`;
    }

    // Owed money is VISIBLE here, never arithmetic: it is not in the shortfall,
    // not netted off `available`, and settling it changes neither (owner ruling
    // 2026-07-19 — due adjustments never reduce any balance figure). One line,
    // stating only what the panel does not already show.
    const owed = round2(amountOwed);
    const owedNote = owed > FLOOR_EPSILON
        ? `Separately, ${fmtAed(owed)} in price adjustments is due on your account — it is not part of this shortfall.`
        : null;

    const actions = [];
    if (canPayByCard && shortN > FLOOR_EPSILON) {
        actions.push({
            key: 'card',
            label: `Pay ${fmtAed(shortN)} by card`,
            hint: contribution > FLOOR_EPSILON
                ? `Your wallet covers ${fmtAed(contribution)}; the card pays the rest in one secure payment.`
                : 'One secure payment on the hosted payment page.',
            primary: true,
        });
    }
    actions.push({
        key: 'topup',
        label: 'Top up your wallet',
        hint: 'Top-ups earn bonus credit — the booking continues once the balance is there.',
        primary: !canPayByCard,
    });
    actions.push({
        key: 'reduce',
        label: 'Change the booking',
        hint: 'Fewer hours or cleaners brings the total down.',
        primary: false,
    });

    return {
        cost: costN,
        available: availN,
        contribution,
        shortfall: shortN,
        rows,
        headline,
        why,
        owedNote,
        actions,
        // Said on every floor panel, unconditionally. A block is not a charge.
        assurance: 'Nothing has been charged.',
    };
};

/**
 * The ADMIN half of the same rule (P4). Staff get the shortfall up front and
 * the action disabled with the reason — never a failure on click.
 *
 * Deliberately returns a SENTENCE, not an error: it is rendered as an option
 * subtitle and a button title, both of which must read as guidance.
 */
export const adminShortfallReason = ({ cost = 0, available = 0 } = {}) => {
    const costN = round2(cost);
    const availN = round2(available);
    const short = round2(costN - availN);
    if (!(short > FLOOR_EPSILON)) return null;
    return `${fmtAed(availN)} available — ${fmtAed(short)} short. `
        + 'Charging the wallet would take it below zero, so this option is unavailable. '
        + 'Use a saved card, invoice it later, or ask the client to top up.';
};

export const isAdminWalletShort = ({ cost = 0, available = 0 } = {}) =>
    round2(cost) - round2(available) > FLOOR_EPSILON;
